import {
  PREVIEW_BLOCK_ITEM_TYPE,
  type ExcludeGroup,
  type IncludeGroup,
  type LogicValidation,
  type PreviewBlockItem,
} from '../types/index';
import { getPreviewBlockItemTypeByFilterType } from './query';

import { getLogicalOperatorString } from '@/entities/query/constants';
import { LogicalOperator, type QueryFilterExpression } from '@/entities/query/types';

// Validation and logic preview
export const validateLogic = (
  includeGroups: IncludeGroup[],
  excludeGroup: ExcludeGroup,
): LogicValidation => {
  const warnings: string[] = [];
  const logicPreview: PreviewBlockItem[] = [];

  if (includeGroups.length === 0 || includeGroups.every((group) => group.values.length === 0)) {
    return {
      isValid: false,
      warnings: ['At least one include group is required'],
      logicPreview: [],
    };
  }

  // Build include logic preview
  const includeLogic: PreviewBlockItem[] = [];

  includeGroups.forEach((group, index) => {
    // Add operator between groups
    if (index > 0) {
      const nextOp = includeGroups[index - 1].nextOp;
      if (nextOp) {
        includeLogic.push({
          type: PREVIEW_BLOCK_ITEM_TYPE.AND,
          value: getLogicalOperatorString(nextOp),
        });
      }
    }

    // Build group with parentheses
    if (group.values.length > 0) {
      includeLogic.push({
        type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
        value: '(',
      });

      // Add values within the group
      group.values.forEach((item, itemIndex) => {
        if (itemIndex > 0) {
          includeLogic.push({
            type: PREVIEW_BLOCK_ITEM_TYPE.AND,
            value: getLogicalOperatorString(group.op),
          });
        }

        // Determine if it's a keyword or account based on type
        const itemType = getPreviewBlockItemTypeByFilterType(item.type);
        includeLogic.push({
          type: itemType,
          value: item.value,
        });
      });

      includeLogic.push({
        type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
        value: ')',
      });
    }
  });

  // Build exclude logic preview
  const excludeLogic: PreviewBlockItem[] = [];
  if (excludeGroup.values && excludeGroup.values.length > 0) {
    excludeLogic.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.NOT,
      value: 'NOT',
    });
    excludeLogic.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
      value: '(',
    });

    excludeGroup.values.forEach((item, index) => {
      if (index > 0) {
        excludeLogic.push({
          type: PREVIEW_BLOCK_ITEM_TYPE.OR,
          value: 'OR',
        });
      }

      const itemType = getPreviewBlockItemTypeByFilterType(item.type);
      excludeLogic.push({
        type: itemType,
        value: item.value,
      });
    });

    excludeLogic.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
      value: ')',
    });
  }

  // Combine include and exclude logic
  if (includeLogic.length > 0 && excludeLogic.length > 0) {
    logicPreview.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
      value: '(',
    });
    logicPreview.push(...includeLogic);
    logicPreview.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION,
      value: ')',
    });
    logicPreview.push({
      type: PREVIEW_BLOCK_ITEM_TYPE.AND,
      value: 'AND',
    });
    logicPreview.push(...excludeLogic);
  } else if (includeLogic.length > 0) {
    logicPreview.push(...includeLogic);
  } else if (excludeLogic.length > 0) {
    logicPreview.push(...excludeLogic);
  }

  // Check for mixed AND/OR without proper grouping
  const hasAndOr =
    includeGroups.some((g) => g.nextOp === LogicalOperator.And) &&
    includeGroups.some((g) => g.nextOp === LogicalOperator.Or);

  if (hasAndOr && includeGroups.length > 2) {
    warnings.push('Mixed AND/OR conditions detected. Consider grouping for clarity.');
  }

  // Check for empty groups
  const emptyGroups = includeGroups.filter((g) => g.values.length === 0);
  if (emptyGroups.length > 0) {
    warnings.push(`${emptyGroups.length} group(s) have no keywords and will be ignored.`);
  }

  return {
    isValid: warnings.length === 0 || warnings.every((w) => !w.includes('required')),
    warnings,
    logicPreview,
  };
};

export function isFilterEmpty(filter: QueryFilterExpression): boolean {
  const hasIncludeValues = filter.include.every((include) => include.values.length > 0);
  const hasExcludeValues = filter.exclude.values ? filter.exclude.values.length > 0 : false;
  return !hasIncludeValues && !hasExcludeValues;
}
